import type { ReactNode } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';

import { Sport } from '#src/__generated__/globalTypes.ts';
import { FRONTEND_ASSET_HOST } from '#src/constants/assets.ts';
import { useIsMlbPage } from '#src/hooks/useIsMlbPage.ts';
import { useIsNBAPage } from '#src/hooks/useIsNBAPage.ts';
import { useSharedAcrossSportsPage as useSharedAccrossSportsPage } from '#src/hooks/useSharedAccrossSportsPage.ts';
import { metadatas as baseballMetadatas } from '#src/lib/seo/baseball.ts';
import { metadatas as commonMetadatas } from '#src/lib/seo/common.ts';
import { metadatas as footballMetadatas } from '#src/lib/seo/football.ts';
import { metadatas as nbaMetadatas } from '#src/lib/seo/nba.ts';
import type { ImageVariations } from '#src/lib/share.ts';

import type { Options } from './index.tsx';
import { SeoContext } from './index.tsx';

type Props = {
  children: ReactNode;
};

const sportTitles = {
  [Sport.BASEBALL]: baseballMetadatas.default.title,
  [Sport.NBA]: nbaMetadatas.default.title,
  [Sport.FOOTBALL]: footballMetadatas.default.title,
};

const sportDescriptions = {
  [Sport.BASEBALL]: baseballMetadatas.default.description,
  [Sport.NBA]: nbaMetadatas.default.description,
  [Sport.FOOTBALL]: footballMetadatas.default.description,
};

const defaultImage = `${FRONTEND_ASSET_HOST}/meta/social-picture-2022.png`;
const defaultTwitter = '@sorare';

export const SeoProvider = ({ children }: Props) => {
  const { formatMessage } = useIntl();
  const [title, setTitle] = useState<string | undefined>(undefined);
  const [description, setDescription] = useState<string | undefined>(undefined);
  const [img, setImg] = useState<
    string | Record<ImageVariations, string | null> | undefined | null
  >(undefined);
  const [twitter, setTwitter] = useState<string | undefined>(undefined);
  const sharedAcrossSportsPage = useSharedAccrossSportsPage();

  const setPageMetadata = useCallback(
    (componentTitle: string, options?: Options) => {
      const titleWithNamespace =
        componentTitle.indexOf('•') >= 0 ||
        componentTitle.indexOf('- Sorare') >= 0 ||
        componentTitle.indexOf('– Sorare') >= 0 ||
        componentTitle.indexOf('Sorare') === 0
          ? componentTitle
          : `${componentTitle} • Sorare`;

      const seoTitle = options?.replaceFullTitle
        ? componentTitle
        : titleWithNamespace;

      setTitle(seoTitle);
      setDescription(options?.description);
      setImg(options?.img);
      setTwitter(options?.twitter);
      return () => {
        setTitle(undefined);
        setImg(undefined);
        setDescription(undefined);
        setTwitter(undefined);
      };
    },
    []
  );

  const isMlbPage = useIsMlbPage();
  const isNBAPage = useIsNBAPage();

  let currentSport: Sport = Sport.FOOTBALL;
  if (isMlbPage) currentSport = Sport.BASEBALL;
  if (isNBAPage) currentSport = Sport.NBA;

  const defaultTitle = commonMetadatas.default.title;
  const defaultSportTitle = sportTitles[currentSport];
  const pageTitle =
    title ||
    (sharedAcrossSportsPage
      ? formatMessage(defaultTitle)
      : formatMessage(defaultSportTitle));

  const defaultDescription = commonMetadatas.default.description;
  const defaultSportDescription = sportDescriptions[currentSport];
  const pageDescription =
    description ||
    (sharedAcrossSportsPage
      ? formatMessage(defaultDescription)
      : formatMessage(defaultSportDescription));

  const value = useMemo(
    () => ({
      setPageMetadata,
    }),
    [setPageMetadata]
  );

  return (
    <SeoContext.Provider value={value}>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta property="twitter:site" content={twitter || defaultTwitter} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        {/* oxlint-disable-next-line sorare/no-window-location */}
        <meta property="og:url" content={window.location.href} />
        {!!img && typeof img === 'string' && (
          <meta property="og:image" content={img || defaultImage} />
        )}
        {!!img && typeof img === 'object' && (
          <meta
            property="og:image"
            content={img.post || img.square || img.story || defaultImage}
          />
        )}
      </Helmet>
      {children}
    </SeoContext.Provider>
  );
};
